import type { AuthProviderProps } from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { getHub, realmForHub } from './hubStore';

/**
 * OIDC-PKCE configuration against the engine's live Keycloak (realm-per-hub). The SPA is a PUBLIC
 * client: Authorization Code + PKCE (S256), NO client secret — nothing secret ever lives in source
 * (CLAUDE.md non-negotiable #1). All values come from VITE_* env.
 *
 * Issuer resolution (owner decision OPSFL-PS2-OIDC = option a): the login screen takes a hub SLUG →
 * realm `opsflow-<slug>` → issuer `${VITE_OIDC_ISSUER_BASE}/realms/opsflow-<slug>`. A full
 * `VITE_OIDC_ISSUER` (if set) overrides the slug flow for single-realm deployments.
 *
 * The SPA client id defaults to `onelattice-console` — the client the hub-realm provisioner creates
 * (public, auth-code+PKCE, self-audience mapper `aud=onelattice-console`, which the resource server
 * hard-requires). Do NOT change this default without changing the provisioned client.
 */

/**
 * When true, skip the real OIDC redirect and mount a mock session (local dev only). Reads the
 * build-time literal, never the env at runtime, so a production bundle carries no mock (EST-40).
 */
export function authDisabled(): boolean {
  return __OL_MOCK_AUTH__;
}

export function clientId(): string {
  return import.meta.env.VITE_OIDC_CLIENT_ID || 'onelattice-console';
}

export function issuerBase(): string | undefined {
  return import.meta.env.VITE_OIDC_ISSUER_BASE;
}

export function redirectUri(): string {
  return import.meta.env.VITE_OIDC_REDIRECT_URI || `${window.location.origin}/auth/callback`;
}

/** The full issuer for a given hub slug, or undefined when no base is configured. */
export function issuerForHub(slug: string): string | undefined {
  const base = issuerBase();
  if (!base) return undefined;
  return `${base.replace(/\/$/, '')}/realms/${realmForHub(slug)}`;
}

/**
 * The resolved issuer, in priority order: an explicit full `VITE_OIDC_ISSUER`, else the stored hub
 * slug + `VITE_OIDC_ISSUER_BASE`. Returns undefined when a hub slug is still needed.
 */
export function resolveIssuer(): string | undefined {
  const explicit = import.meta.env.VITE_OIDC_ISSUER;
  if (explicit) return explicit;
  const hub = getHub();
  if (hub) return issuerForHub(hub);
  return undefined;
}

/** True when a real issuer can be built without asking for a hub slug. */
export function needsHubSlug(): boolean {
  return !import.meta.env.VITE_OIDC_ISSUER && !getHub();
}

export function oidcSettings(authority: string): AuthProviderProps {
  return {
    authority,
    client_id: clientId(),
    redirect_uri: redirectUri(),
    post_logout_redirect_uri: window.location.origin,
    scope: import.meta.env.VITE_OIDC_SCOPE || 'openid profile email',
    response_type: 'code', // Authorization Code + PKCE (oidc-client-ts uses S256 for public clients)
    automaticSilentRenew: true,
    loadUserInfo: true,
    // Tokens in sessionStorage (cleared on tab close) — never localStorage for access tokens.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // Strip the ?code&state from the URL after a successful callback.
    onSigninCallback: () => {
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };
}
